import { useEffect, useState } from 'react';
import { cassa, scontrini } from '../../api/scontrini';
import type {
  DettaglioScontrino,
  IncassoGiorno,
  ParametriScontriniLetti,
  SituazioneCassa,
  SituazioneTecnico,
} from '../../api/scontrini';
import { formattaData, formattaDataOra, formattaImporto, oggiIso } from '../../lib/formato';
import { numeroDecimale } from '../../lib/numeri';

/*  LA CASSA DI NOVARA (22/10/2026). Per chi la tiene — Nicolò, amministratore: quanto c'e' dentro e lo
    ritira; chi deve ancora versare; gli scontrini rimasti a meta' da sistemare; gli
    incassi per giorno e tecnico; i parametri.  */
export function CassaNovara() {
  const [situazione, setSituazione] = useState<SituazioneCassa | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  const carica = () => {
    setErrore(null);
    cassa
      .situazione()
      .then(setSituazione)
      .catch((e: Error) => setErrore(e.message));
  };
  useEffect(carica, []);

  return (
    <section className="pagina cassa-novara">
      <header className="testata-pagina">
        <div>
          <h1>Cassa Novara</h1>
          <p>
            I contanti versati dai tecnici caldaie. Quando li ritiri la cassa torna a zero; chi deve ancora versare
            si vede sotto, con quanto risulta dai suoi scontrini in contanti.
          </p>
        </div>
      </header>

      {errore && <p className="errore">{errore}</p>}
      {situazione && <Ritiro situazione={situazione} onFatto={setSituazione} />}
      {situazione && situazione.sospesi.length > 0 && <Sospesi elenco={situazione.sospesi} onCambiato={carica} />}
      {situazione && <Tecnici elenco={situazione.tecnici} onCambiato={carica} />}
      <Incassi />
      {situazione && situazione.movimenti.length > 0 && (
        <div className="riquadro-cassa">
          <h2>Movimenti</h2>
          <div className="tabella-contenitore">
            <table>
              <thead>
                <tr>
                  <th>Quando</th>
                  <th>Cosa</th>
                  <th>Tecnico</th>
                  <th className="destra">Importo</th>
                  <th className="destra">Atteso</th>
                  <th>Note</th>
                  <th>Da</th>
                </tr>
              </thead>
              <tbody>
                {situazione.movimenti.map((m) => (
                  <tr key={m.id}>
                    <td>{formattaDataOra(m.creatoIl)}</td>
                    <td>{m.tipo === 'Ritiro' ? 'Ritiro' : 'Versamento'}</td>
                    <td>{m.tecnico ?? '—'}</td>
                    <td className="destra">{formattaImporto(m.importo)}</td>
                    <td className={m.importo !== m.atteso ? 'destra differenza' : 'destra'}>
                      {formattaImporto(m.atteso)}
                    </td>
                    <td>{m.note ?? ''}</td>
                    <td>{m.creatoDa}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      <Parametri />
    </section>
  );
}

function Ritiro({ situazione, onFatto }: { situazione: SituazioneCassa; onFatto: (s: SituazioneCassa) => void }) {
  const [contati, setContati] = useState('');
  const [note, setNote] = useState('');
  const [inCorso, setInCorso] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => setContati(situazione.saldo > 0 ? String(situazione.saldo).replace('.', ',') : ''), [situazione.saldo]);

  const valore = numeroDecimale(contati);

  async function ritira() {
    if (valore === null || valore <= 0) return;
    setInCorso(true);
    setErrore(null);
    try {
      onFatto(await cassa.ritira({ importo: valore, note: note.trim() === '' ? null : note.trim() }));
      setNote('');
    } catch (e) {
      setErrore((e as Error).message);
    } finally {
      setInCorso(false);
    }
  }

  return (
    <div className="riquadro-cassa saldo">
      <p className="totale-ore">
        In cassa <strong>{formattaImporto(situazione.saldo)}</strong>
        {situazione.ultimoRitiroIl && (
          <span className="nota"> · ultimo ritiro {formattaDataOra(situazione.ultimoRitiroIl)}</span>
        )}
      </p>
      {situazione.saldo > 0 && (
        <div className="riga-azione">
          <label className="campo-blocco">
            <span>Contati</span>
            <input inputMode="decimal" value={contati} onChange={(e) => setContati(e.target.value)} />
          </label>
          <label className="campo-blocco lungo">
            <span>Note</span>
            <input
              value={note}
              placeholder={valore !== null && valore !== situazione.saldo ? 'la differenza va spiegata' : ''}
              onChange={(e) => setNote(e.target.value)}
            />
          </label>
          <button
            type="button"
            className="primario"
            disabled={inCorso || valore === null || valore <= 0}
            onClick={() => void ritira()}
          >
            Ritiro la cassa
          </button>
        </div>
      )}
      {errore && <p className="errore">{errore}</p>}
    </div>
  );
}

function Tecnici({ elenco, onCambiato }: { elenco: SituazioneTecnico[]; onCambiato: () => void }) {
  const [errore, setErrore] = useState<string | null>(null);

  async function registra(t: SituazioneTecnico) {
    const testo = window.prompt(`Quanto ha versato ${t.tecnico}?`, String(t.daVersare).replace('.', ','));
    const valore = numeroDecimale(testo);
    if (valore === null || valore <= 0) return;
    setErrore(null);
    try {
      await cassa.versaPer(t.tecnicoId, { importo: valore, note: 'Registrato dalla cassa' });
      onCambiato();
    } catch (e) {
      setErrore((e as Error).message);
    }
  }

  return (
    <div className="riquadro-cassa">
      <h2>Tecnici</h2>
      {elenco.length === 0 && <p className="nota">Nessun incasso in contanti finora.</p>}
      {elenco.length > 0 && (
        <div className="tabella-contenitore">
          <table>
            <thead>
              <tr>
                <th>Tecnico</th>
                <th className="destra">Contanti incassati</th>
                <th className="destra">Versati</th>
                <th className="destra">Da versare</th>
                <th>Ultimo versamento</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {elenco.map((t) => (
                <tr key={t.tecnicoId}>
                  <td>{t.tecnico}</td>
                  <td className="destra">{formattaImporto(t.contantiIncassati)}</td>
                  <td className="destra">{formattaImporto(t.versati)}</td>
                  <td className="destra">
                    <strong>{formattaImporto(t.daVersare)}</strong>
                  </td>
                  <td>{t.ultimoVersamentoIl ? formattaDataOra(t.ultimoVersamentoIl) : '—'}</td>
                  <td>
                    {t.daVersare > 0 && (
                      <button type="button" onClick={() => void registra(t)}>
                        Registra versamento
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {errore && <p className="errore">{errore}</p>}
    </div>
  );
}

function Sospesi({ elenco, onCambiato }: { elenco: DettaglioScontrino[]; onCambiato: () => void }) {
  const [errore, setErrore] = useState<string | null>(null);

  async function fai(azione: () => Promise<unknown>) {
    setErrore(null);
    try {
      await azione();
      onCambiato();
    } catch (e) {
      setErrore((e as Error).message);
    }
  }

  return (
    <div className="riquadro-cassa sospesi">
      <h2>Scontrini da sistemare</h2>
      <p className="nota">
        «Da verificare»: la stampante non ha risposto e potrebbe averlo emesso. Si guarda sulla stampante di Novara
        (o nel giornale elettronico) e si dice com’è andata.
      </p>
      <div className="tabella-contenitore">
        <table>
          <thead>
            <tr>
              <th>Numero</th>
              <th>Quando</th>
              <th>Tecnico</th>
              <th>Pagamento</th>
              <th className="destra">Totale</th>
              <th>Stato</th>
              <th>Errore</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {elenco.map((s) => (
              <tr key={s.id}>
                <td>{s.numero}</td>
                <td>{formattaDataOra(s.creatoIl)}</td>
                <td>{s.tecnico}</td>
                <td>{s.pagamento === 'Contanti' ? 'contanti' : 'POS'}</td>
                <td className="destra">{formattaImporto(s.totale)}</td>
                <td>{s.stato === 'DaVerificare' ? 'da verificare' : 'da emettere'}</td>
                <td className="errore-breve">{s.ultimoErrore ?? ''}</td>
                <td className="azioni-riga">
                  {s.stato === 'DaVerificare' && (
                    <>
                      <button
                        type="button"
                        onClick={() =>
                          void fai(async () => {
                            const esito = await scontrini.verifica(s.id);
                            window.alert(esito.messaggio);
                          })
                        }
                      >
                        Controlla sulla stampante
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const numero = window.prompt('Numero del documento commerciale sulla stampante:');
                          if (numero !== null) void fai(() => scontrini.uscito(s.id, numero.trim() || null));
                        }}
                      >
                        È uscito
                      </button>
                      <button type="button" onClick={() => void fai(() => scontrini.nonUscito(s.id))}>
                        Non è uscito
                      </button>
                    </>
                  )}
                  {s.stato === 'DaEmettere' && (
                    <>
                      <button type="button" onClick={() => void fai(() => scontrini.riprova(s.id))}>
                        Riprova
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const motivo = window.prompt('Perché si annulla?');
                          if (motivo?.trim()) void fai(() => scontrini.annulla(s.id, motivo.trim()));
                        }}
                      >
                        Annulla
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {errore && <p className="errore">{errore}</p>}
    </div>
  );
}

function Incassi() {
  const settimana = () => {
    const d = new Date();
    d.setDate(d.getDate() - 6);
    return d.toISOString().slice(0, 10);
  };
  const [dal, setDal] = useState(settimana);
  const [al, setAl] = useState(oggiIso);
  const [righe, setRighe] = useState<IncassoGiorno[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);

  useEffect(() => {
    setErrore(null);
    scontrini
      .incassi(dal, al)
      .then(setRighe)
      .catch((e: Error) => setErrore(e.message));
  }, [dal, al]);

  const contanti = (righe ?? []).reduce((t, r) => t + r.contanti, 0);
  const pos = (righe ?? []).reduce((t, r) => t + r.pos, 0);

  return (
    <div className="riquadro-cassa">
      <h2>Incassi per giorno</h2>
      <div className="riga-azione">
        <label className="campo-blocco">
          <span>Dal</span>
          <input type="date" value={dal} onChange={(e) => setDal(e.target.value)} />
        </label>
        <label className="campo-blocco">
          <span>Al</span>
          <input type="date" value={al} onChange={(e) => setAl(e.target.value)} />
        </label>
        <p className="totale-ore">
          Contanti <strong>{formattaImporto(contanti)}</strong> · POS <strong>{formattaImporto(pos)}</strong>
        </p>
      </div>
      {errore && <p className="errore">{errore}</p>}
      {righe && righe.length === 0 && <p className="nota">Nessuno scontrino emesso nel periodo.</p>}
      {righe && righe.length > 0 && (
        <div className="tabella-contenitore">
          <table>
            <thead>
              <tr>
                <th>Giorno</th>
                <th>Tecnico</th>
                <th className="destra">Scontrini</th>
                <th className="destra">Contanti</th>
                <th className="destra">POS</th>
              </tr>
            </thead>
            <tbody>
              {righe.map((r) => (
                <tr key={`${r.data}-${r.tecnicoId}`}>
                  <td>{formattaData(r.data)}</td>
                  <td>{r.tecnico}</td>
                  <td className="destra">{r.scontrini}</td>
                  <td className="destra">{formattaImporto(r.contanti)}</td>
                  <td className="destra">{formattaImporto(r.pos)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Parametri() {
  const [p, setP] = useState<ParametriScontriniLetti | null>(null);
  const [aperti, setAperti] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [salvato, setSalvato] = useState(false);

  useEffect(() => {
    cassa
      .parametri()
      .then(setP)
      .catch((e: Error) => setErrore(e.message));
  }, []);

  if (!p) return errore ? <p className="errore">{errore}</p> : null;

  const campo = (chiave: keyof ParametriScontriniLetti, etichetta: string, tipo: 'testo' | 'numero' = 'testo') => (
    <label className="campo-blocco">
      <span>{etichetta}</span>
      <input
        inputMode={tipo === 'numero' ? 'decimal' : undefined}
        value={String(p[chiave] ?? '')}
        onChange={(e) => {
          setSalvato(false);
          setP({ ...p, [chiave]: tipo === 'numero' ? (numeroDecimale(e.target.value) ?? 0) : e.target.value });
        }}
      />
    </label>
  );

  async function salva() {
    if (!p) return;
    setErrore(null);
    try {
      setP(
        await cassa.scriviParametri({
          articoli: p.articoli,
          listinoMago: p.listinoMago?.trim() || null,
          aliquotaIva: p.aliquotaIva,
          repartoIva: Math.round(p.repartoIva),
          limiteContanti: p.limiteContanti,
          oraChiusura: p.oraChiusura,
          minutiAttornoChiusura: Math.round(p.minutiAttornoChiusura),
          codiceSede: p.codiceSede,
          contoCassa: p.contoCassa?.trim() || null,
          contoPosTransitorio: p.contoPosTransitorio?.trim() || null,
          contoPos: p.contoPos?.trim() || null,
          modoCopia: p.modoCopia,
        }),
      );
      setSalvato(true);
    } catch (e) {
      setErrore((e as Error).message);
    }
  }

  return (
    <details className="riquadro-cassa" open={aperti} onToggle={(e) => setAperti(e.currentTarget.open)}>
      <summary>
        <h2>Parametri degli scontrini</h2>
      </summary>
      <p className="nota">
        Stampante sul server: {p.stampante ?? 'nessuna — gli scontrini non si possono emettere'}.
        {!p.listinoMago && ' Manca il listino di Mago: senza, i prezzi non si leggono.'}
      </p>
      <div className="griglia-parametri">
        {campo('articoli', 'Articoli (codici Mago, separati da virgola)')}
        {campo('listinoMago', 'Listino di Mago')}
        {campo('aliquotaIva', 'Aliquota IVA %', 'numero')}
        {campo('repartoIva', 'Reparto della stampante', 'numero')}
        {campo('limiteContanti', 'Limite contanti €', 'numero')}
        {campo('oraChiusura', 'Ora chiusura stampante')}
        {campo('minutiAttornoChiusura', 'Minuti di blocco attorno alla chiusura', 'numero')}
        {campo('codiceSede', 'Sede (in coda al numero)')}
        {campo('contoCassa', 'Conto cassa')}
        {campo('contoPosTransitorio', 'Conto transitorio POS')}
        {campo('contoPos', 'Conto incasso POS')}
        <label className="spunta-parametro">
          <input
            type="checkbox"
            checked={p.modoCopia === 'ScontrinoDigitale'}
            onChange={(e) => {
              setSalvato(false);
              setP({ ...p, modoCopia: e.target.checked ? 'ScontrinoDigitale' : 'CopiaTeraWorks' });
            }}
          />
          <span>
            <strong>Scontrino smart Epson</strong>
            <small>
              {p.modoCopia === 'ScontrinoDigitale'
                ? 'Niente carta: la stampante manda lo scontrino digitale alla mail del cliente (o del tecnico).'
                : 'Spento: lo scontrino esce di carta a Novara e TeraWorks ne manda una copia da erp@.'}
            </small>
          </span>
        </label>
      </div>
      {errore && <p className="errore">{errore}</p>}
      <p className="azioni">
        <button type="button" className="primario" onClick={() => void salva()}>
          Salva i parametri
        </button>
        {salvato && <span className="nota">Salvati.</span>}
      </p>
    </details>
  );
}
